import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, Optional, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { isArray, isEmpty } from 'lodash';
import * as moment from 'moment';
import { Moment } from 'moment';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { EVENT_OCCERRENCE_TIME_SC, RECEIVE_TIME_SC } from 'src/app/common/const';
import { ToastService } from 'src/app/common/service/toast.service';
import { MapEventType } from 'src/app/map/model/map-event';
import { MapEventService } from 'src/app/map/service/map-event.service';
import { FieldType } from 'src/app/settings/model/field-type';
import { UserSettingsService } from 'src/app/settings/service/user-settings.service';
import { Option, ValueField } from '../../../../settings/model/value-field';
import { CustomValidator } from '../../../model/custom-validator';
import { Operator } from '../../../model/operator';
import { RequestCriteria } from '../../../model/request-criteria';
import { getOperators } from '../../../util/data-source-util';
import '../../../util/date-util';
import { getErrorsForSetOfFilters, markAsTouched, setFormGroupControlsErrors, validateAllFormFields } from '../../../util/validation-util';
import { FilterListFieldType } from '../../model/filter-list-field-type';
import { ColumnValuesProvider } from '../../../service/column-values-provider.service';
import { FilterBuilderService } from '../../service/filter-builder.service';
import { FilterValidation } from './filter-validation';
import { DepartureFields } from 'src/app/sakp/model/departure';
import { VehicleGroupDialogComponent } from 'src/app/vehicles/component/vehicle-group-dialog/vehicle-group-dialog.component';
import { ConfirmationDialogComponent } from 'src/app/common/dialog/confirmation/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FilterBuilderListFieldComponent } from '../filter-builder-list-field/filter-builder-list-field.component';
import { FilterBuilderFieldSelectComponent } from '../filter-builder-field-select/filter-builder-field-select.component';
import { FilterDataProvider } from '../../service/filter-data-provider';

@Component({
  selector: 'app-filter-builder',
  templateUrl: './filter-builder.component.html',
  styleUrls: ['./filter-builder.component.scss']
})
export class FilterBuilderComponent implements OnInit, OnDestroy {

  private static readonly HARD_LIMIT_VALUES = 50;
  private static readonly HARD_LIMIT_CRITERIA = 20;

  @ViewChildren(FilterBuilderListFieldComponent)
  filterBuilderListFieldComponents: QueryList<FilterBuilderListFieldComponent>;

  @ViewChildren(FilterBuilderFieldSelectComponent)
  filterBuilderFieldSelectComponents: QueryList<FilterBuilderFieldSelectComponent>;

  @Input()
  showLexicografically: boolean;

  @Input()
  selectedRows: any[] | null = null;

  form: FormGroup;
  filterIterator: number = 1;
  fields: ValueField[];
  filterValidation: FilterValidation;

  minDate: Date;
  maxDate: Date;

  private unsubscribe$ = new Subject();

  currentDrawingPolygonKey: string;
  readOnlyMode: boolean = false;

  constructor(private toastService: ToastService, private formBuilder: FormBuilder, private changeDetector: ChangeDetectorRef,
    private filterBuilderService: FilterBuilderService, private settings: UserSettingsService,
    private columnValuesProvider: ColumnValuesProvider,
    private dialog: MatDialog, public translate: TranslateService, private filterDataProvider: FilterDataProvider,
    @Optional() private mapEventService?: MapEventService
  ) {
    this.initForm();
    this.minDate = moment().subtract(1, 'year').startOf('year').toDate();
    this.maxDate = moment().endOf('day').toDate();
  }

  ngOnInit(): void {
    this.mapEventService?.events$
      .pipe(filter(event => event.type == MapEventType.SELECTED_POLYGON && this.form?.controls[event.data.key] != null), takeUntil(this.unsubscribe$))
      .subscribe(event => this.polygonChanged(event.data.key, event.data.points));

    this.filterBuilderService.filtersRequest$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        let filters = null;
        if (this.validateForSetOfFilters())
          filters = this.getFilters();
        return this.filterBuilderService.putFilters(filters);
      });

    this.filterBuilderService.initFilters$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(initFilters => {
        if (initFilters && initFilters.fieldAndOperatorReadOnly)
          this.setReadOnlyMode(initFilters);
        else
          this.setInitialFilters(initFilters, true);
      });
    this.translate.onLangChange
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          if (this.showLexicografically) {
            this.updateFields(this.fields);
          }
          this.form.patchValue(this.form.getRawValue(), { emitEvent: false });
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private initForm(): void {
    if (!this.form)
      this.form = this.formBuilder.group({});
    else {
      const controls = Object.keys(this.form.controls);
      controls.forEach(control => this.removeFilter(control));
      this.form.markAsPristine();
    }

    let prevCustomValidators = null;
    if (this.filterValidation)
      prevCustomValidators = this.filterValidation.formCustomValidators;

    this.filterValidation = new FilterValidation(this.form);
    if (prevCustomValidators)
      this.filterValidation.setCustomValidators(prevCustomValidators);
  }

  setFields(fields: ValueField[]): void {
    this.initForm();
    this.updateFields(fields);
    this.changeDetector.detectChanges();
  }

  updateFields(fields: ValueField[]): void {
    const filterable = fields.filter(field => field.canFilter);
    this.fields = this.showLexicografically ?
      filterable
        .sort((a, b) => (this.translate.instant(this.getValueFieldName(a)) as string).localeCompare(this.translate.instant(this.getValueFieldName(b)))) :
      filterable;
  }

  setFormValidators(customValidators: CustomValidator[] | null): void {
    this.filterValidation.setCustomValidators(customValidators);
  }

  setInitialFilters(initialFilters: RequestCriteria, cleanBefore: boolean = false): void {
    this.readOnlyMode = false;
    if (cleanBefore)
      this.initForm();
    this.addFiltersIfInitialFilters(initialFilters);
  }

  get formFilters(): string[] {
    return Object.keys(this.form.controls);
  }

  get formErrors(): string[] {
    return Object.keys(this.form.errors);
  }

  getFieldGroupValue(groupName: string): AbstractControl | null {
    return this.form.get(groupName)?.get(FilterValidation.controlField);
  }

  getFieldGroupArray(groupName: string): FormArray {
    return this.form.get(groupName)?.get(FilterValidation.controlValue) as FormArray;
  }

  getSelectedOption(groupName: string, idx: number): Option | undefined {
    const options = this.getFieldOptions(groupName);
    const value = this.getFieldGroupArray(groupName)?.controls[idx]?.value;
    return options?.find(o => o.key === value);
  }

  // Kolumny bez słownika, ale z listą wartości budowaną z danych (ValueField.filterOptionsFromData),
  // dają wybór z listy zamiast wpisywania z palca - ale tylko przy równy/nierówny, bo tylko tam
  // wartość ma się zgadzać w całości, i tylko gdy widoczna tabela ma w pamięci całość danych
  // (patrz ColumnValuesProvider).
  //
  // Warianty "i" muszą tu być: dla kolumn tekstowych - a takie są wszystkie oflagowane - builder
  // oferuje wyłącznie iEquals/iNotEqual (patrz getOperators), więc sama para equals/notEqual nie
  // dopasowałaby się nigdy.
  private static readonly VALUE_LIST_OPERATORS: Operator[] = [
    Operator.equals, Operator.notEqual, Operator.iEquals, Operator.iNotEqual
  ];

  getFieldOptions(groupName: string): Option[] | null {
    const field: ValueField = this.getFieldGroupValue(groupName)?.value;
    if (!field) return null;
    // Wartości z wczytanej doby mają pierwszeństwo przed słownikiem pola i przed listami
    // dociąganymi z serwera: pokazują dokładnie to, co jest w danych, na które patrzy użytkownik.
    return this.dataColumnOptions(field, groupName) ?? field.options ?? null;
  }

  private dataColumnOptions(field: ValueField, groupName: string): Option[] | null {
    if (!field.filterOptionsFromData) return null;
    const operator = this.form.get(groupName)?.get(FilterValidation.controlOperator)?.value;
    if (!FilterBuilderComponent.VALUE_LIST_OPERATORS.includes(operator)) return null;
    const values = this.columnValuesProvider.getValues(field.name);
    return values.length ? values : null;
  }

  removeArrayElement(groupName: string, index: number): void {
    this.getFieldGroupArray(groupName).removeAt(index);
  }

  addArrayElement(groupName: string): void {
    const control = new FormControl('');
    this.getFieldGroupArray(groupName).push(control);
    control.markAsTouched();
  }

  addDateArrayElement(groupName: string): void {
    const control = new FormControl(null);
    this.getFieldGroupArray(groupName).push(control);
    control.markAsTouched();
  }

  addDatetimeArrayElement(groupName: string): void {
    const control = new FormControl(null);
    this.getFieldGroupArray(groupName).push(control);
    control.markAsTouched();
  }

  addBooleanArrayElement(groupName: string): void {
    const control = new FormControl(null);
    this.getFieldGroupArray(groupName).push(control);
    control.markAsTouched();
  }

  onDateArrayValueChanged(date: Moment, groupKey: string, idx: number, fieldType: FieldType): void {
    if (idx === 0)
      this.onDateValueChanged(date, groupKey, FilterValidation.controlStart, fieldType);
  }

  getFieldGroupOperator(groupName: string): AbstractControl | null {
    return this.form.get(groupName)?.get(FilterValidation.controlOperator);
  }

  getFieldOperators(valueField: ValueField): Operator[] {
    return getOperators(valueField.type, valueField.jsonpath);
  }

  isListFieldType(groupName: string): boolean {
    return this.getListFieldType(groupName) != null;
  }

  getValueFieldName(value: ValueField): string {
    if (this.settings.isEventOccurenceTimeEnabled() && value.name == RECEIVE_TIME_SC) {
      return EVENT_OCCERRENCE_TIME_SC;
    }
    return value.title ?? value.name;
  }

  onFilterFieldChanged(event: ValueField, groupKey: string): void {
    const selected: ValueField = event;
    const filterGroup = this.form.get(groupKey);
    if (selected) {
      const operators = this.getFieldOperators(selected);
      if (operators && operators.length > 0) {
        filterGroup.get(FilterValidation.controlOperator).setValue(operators[0]);
      }
    }
    const control = filterGroup.get(FilterValidation.controlValue);
    if (control instanceof FormArray) {
      const length = control.controls.length;
      for (let i = 0; i < length; i++)
        control.removeAt(0);
      if (selected.type == FieldType.BOOLEAN)
        control.push(new FormControl(false));
      else if (selected.type == FieldType.DATE)
        control.push(new FormControl(moment().startOf('day')));
      else if (selected.type == FieldType.DATETIME)
        control.push(new FormControl(moment().startOf('day')));
      else
        control.push(new FormControl(''));
    } else {
      if (selected.type == FieldType.BOOLEAN)
        control.setValue(false);
      else
        control.setValue(null);
    }

    switch (selected.type) {
      case FieldType.DATETIME:
      case FieldType.DATE:
        const options = { emitEvent: true, emitModelToViewChange: true, emitViewToModelChange: true };
        filterGroup.get(FilterValidation.controlStart).setValue(moment().startOf('day'), options);
        filterGroup.get(FilterValidation.controlEnd).setValue(moment().endOf('day'), options);
        this.onDateValueChanged(filterGroup.get(FilterValidation.controlStart).value, groupKey, FilterValidation.controlStart, selected.type);
        this.onDateValueChanged(filterGroup.get(FilterValidation.controlEnd).value, groupKey, FilterValidation.controlEnd, selected.type);
        break;
      case FieldType.TIME:
        filterGroup.get(FilterValidation.controlStart).setValue('00:00');
        filterGroup.get(FilterValidation.controlEnd).setValue('23:59');
        break;
      default:
        setFormGroupControlsErrors(null, filterGroup, FilterValidation.controlsStartEndToShow);
        break;
    }
    this.changeDetector.detectChanges();
  }

  isMultiValueOperator(op: Operator): boolean {
    return [Operator.equals, Operator.iEquals, Operator.iContains, Operator.iStartsWith, Operator.iEndsWith].includes(op);
  }

  isBetweenOperator(op: Operator): boolean {
    return [Operator.between, Operator.betweenInclusive].includes(op);
  }

  isNegativeMultivalueOperator(op: Operator): boolean {
    return [Operator.notEqual, Operator.iNotEqual, Operator.notContains, Operator.iNotContains].includes(op);
  }

  private addCopiedNegativeCriteria(groupName: string, field: ValueField, operator: Operator, newValues: string[]): void {
    const CONFIRM_THRESHOLD = 5;

    let existingCriteriaCount = 0;
    const existingValues = new Set<string>();
    this.formFilters.forEach(key => {
      const fg = this.form.get(key);
      const existingField: ValueField = fg?.get(FilterValidation.controlField)?.value;
      const existingOp: Operator = fg?.get(FilterValidation.controlOperator)?.value;
      if (existingField?.name === field.name && existingOp === operator) {
        existingCriteriaCount++;
        const arr = fg.get(FilterValidation.controlValue)?.value as any[];
        if (Array.isArray(arr))
          arr.filter(v => v != null && v !== '').forEach(v => existingValues.add(String(v)));
      }
    });

    const toAdd = newValues.filter(v => !existingValues.has(v));
    if (!toAdd.length) return;

    if (existingCriteriaCount + toAdd.length > FilterBuilderComponent.HARD_LIMIT_CRITERIA) {
      this.toastService.showWarnToast(
        'copyValuesFromSelectionTooManyCriteria', null,
        { total: existingCriteriaCount + toAdd.length, max: FilterBuilderComponent.HARD_LIMIT_CRITERIA }
      );
      return;
    }

    const doAdd = (): void => {
      const currentArray = this.getFieldGroupArray(groupName);
      const currentVal = currentArray.controls[0]?.value;
      const currentIsEmpty = currentVal == null || currentVal === '';
      const fieldName = field.fieldName || field.name;
      let startIdx = 0;
      if (currentIsEmpty) {
        currentArray.controls[0]?.setValue(toAdd[0]);
        startIdx = 1;
      }
      toAdd.slice(startIdx).forEach(v => this.addFilter({ fieldName, operator, value: v } as RequestCriteria));
      this.changeDetector.detectChanges();
    };

    if (toAdd.length > CONFIRM_THRESHOLD) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: { message: 'copyValuesFromSelectionManyFilters', messageParams: { count: toAdd.length } }
      });
      dialogRef.afterClosed().subscribe((confirmed: boolean) => { if (confirmed) doAdd(); });
    } else {
      doAdd();
    }
  }

  onFilterOperatorChanged(event: MatSelectChange, groupKey: string): void {
    const selected: Operator = event.value;
    const filterGroup = this.form.get(groupKey) as FormGroup;
    const fieldType: FieldType = this.getFieldGroupValue(groupKey)?.value?.type;
    if (selected && !this.isMultiValueOperator(selected)) {
      const control = filterGroup.get(FilterValidation.controlValue);
      if (control instanceof FormArray) {
        const length = control.controls.length;
        for (let i = 0; i < length - 1; i++)
          control.removeAt(1);
      }
    }
    if (selected !== Operator.equals && (fieldType === FieldType.DATE || fieldType === FieldType.DATETIME)) {
      const startControl = filterGroup.get(FilterValidation.controlStart);
      // Data wybrana przy "równe" siedzi w kryterium 'value' - bez przepisania jej do 'start'
      // zakres poszedłby do zapytania (i do walidacji) z nieaktualnej daty poprzedniego operatora.
      const pickedValue = (filterGroup.get(FilterValidation.controlValue) as FormArray)?.controls[0]?.value;
      if (moment.isMoment(pickedValue) && pickedValue.isValid()) {
        startControl.setValue(pickedValue);
        this.onDateValueChanged(pickedValue, groupKey, FilterValidation.controlStart, fieldType);
      }
      const endControl = filterGroup.get(FilterValidation.controlEnd);
      if (!endControl.value) {
        endControl.setValue(startControl.value);
        this.onDateValueChanged(startControl.value, groupKey, FilterValidation.controlEnd, fieldType);
      }
    }
    if (selected === Operator.equals && (fieldType === FieldType.DATE || fieldType === FieldType.DATETIME)) {
      const valueArray = filterGroup.get(FilterValidation.controlValue) as FormArray;
      const startValue = filterGroup.get(FilterValidation.controlStart).value;
      if (valueArray?.controls?.length && startValue)
        valueArray.controls[0].setValue(startValue);
    }
    filterGroup.setValidators(this.filterValidation.getFilterGroupValidator());
  }

  onDateValueChanged(date: Moment | null, groupKey: string, dateType: 'start' | 'end', fieldType: FieldType): void {
    const formGroup = this.form.get(groupKey);
    if (date != null) {
      formGroup.get(`${dateType}ToShow`).setValue(this.getFormattedDateForFieldType(date, fieldType));
      const otherDateType = dateType === 'start' ? 'end' : 'start';
      const otherValue = formGroup.get(otherDateType).value as Moment;
      const rangeInvalid = fieldType === FieldType.DATE && otherValue && (
        dateType === 'start' ? date.isAfter(otherValue) : date.isBefore(otherValue)
      );
      if (rangeInvalid) {
        formGroup.get(otherDateType).setValue(date, { emitEvent: false });
        formGroup.get(`${otherDateType}ToShow`).setValue(this.getFormattedDateForFieldType(date, fieldType));
      }
    } else {
      formGroup.get(`${dateType}ToShow`).setValue(null);
    }
    markAsTouched(formGroup, FilterValidation.controlsStartEndToShow);
  }

  drawPolygon(key: string): void {
    this.mapEventService?.enableDrawPolygon(key);
    this.currentDrawingPolygonKey = key;
    const controlValue = this.form.get(key)?.get(FilterValidation.controlValue);
    if (controlValue instanceof FormArray)
      controlValue.controls[0].setValue(null);
    else
      controlValue.setValue(null);
    validateAllFormFields(this.form);
  }

  cancelDrawPolygon(key: string): void {
    this.mapEventService?.cancelDrawPolygon(key);
    this.mapEventService?.clearPolygon(key);
    this.currentDrawingPolygonKey = null;
    const controlValue = this.form.get(key)?.get(FilterValidation.controlValue);
    if (controlValue) {
      if (controlValue instanceof FormArray)
        controlValue.controls[0].setValue(null);
      else
        controlValue.setValue(null);
      validateAllFormFields(this.form);
    } else
      console.error('Form key not exists:', key);
  }

  confirmDrawPolygon(key: string): void {
    this.mapEventService?.cancelDrawPolygon(key);
    this.currentDrawingPolygonKey = null;
  }

  private polygonChanged(key: string, points: L.LatLng[]): void {
    const controlValue = this.form.get(key)?.get(FilterValidation.controlValue);
    if (controlValue) {
      const simplePoints = points.map(point => `${point.lng} ${point.lat}`);
      simplePoints.push(simplePoints[0]);
      const pointsString = simplePoints.join(',');
      if (controlValue instanceof FormArray)
        controlValue.controls[0].setValue(`POLYGON((${pointsString}))`);
      else
        controlValue.setValue(`POLYGON((${pointsString}))`);
    } else {
      console.error('Form key not exists:', key);
    }
  }

  addFilter(criteria: RequestCriteria = null, values: string[] = null): void {
    let valueField = null;
    let criteriaStart = null;
    let criteriaEnd = null;
    if (criteria) {
      valueField = this.getValueFieldByName(criteria.fieldName);
      if (valueField) {
        switch (valueField.type) {
          case FieldType.DATETIME:
          case FieldType.DATE:
            if (criteria.operator === Operator.equals && criteria.value) {
              criteriaStart = moment(criteria.value);
            } else {
              if (criteria.start)
                criteriaStart = moment(criteria.start);
              if (criteria.end)
                criteriaEnd = moment(criteria.end);
            }
            break;
          case FieldType.TIME:
            criteriaStart = criteria.start;
            criteriaEnd = criteria.end;
            break;
          case FieldType.INTEGER:
          case FieldType.FLOAT:
            if (criteria.operator === Operator.between || criteria.operator === Operator.betweenInclusive) {
              if (criteria.start != null) criteriaStart = String(criteria.start);
              if (criteria.end != null) criteriaEnd = String(criteria.end);
            }
            break;
        }
      }
    }

    let valueControls: FormControl[];
    const isDateLikeEquals = (valueField?.type === FieldType.DATE || valueField?.type === FieldType.DATETIME) && criteria?.operator === Operator.equals;
    if (values?.length) {
      if (isDateLikeEquals)
        valueControls = values.filter(v => v != null).map(v => new FormControl(moment(v)));
      else
        valueControls = values.map(value => new FormControl(value));
    } else {
      if (criteria?.value != null) {
        if (isDateLikeEquals)
          valueControls = [new FormControl(moment(criteria.value))];
        else
          valueControls = [new FormControl(criteria?.value)];
      } else {
        valueControls = [new FormControl('')];
      }
    }

    const controls = {};
    controls[FilterValidation.controlField] = new FormControl({ value: valueField || '', disabled: this.readOnlyMode } || '', [Validators.required]);
    controls[FilterValidation.controlOperator] = new FormControl({ value: criteria?.operator || '', disabled: this.readOnlyMode }, [Validators.required]);
    controls[FilterValidation.controlValue] = new FormArray(valueControls);
    controls[FilterValidation.controlStart] = new FormControl(criteriaStart || '');
    controls[FilterValidation.controlEnd] = new FormControl(criteriaEnd || '');
    const startForDisplay = (criteria?.operator === Operator.equals && criteria?.value) ? criteria.value : criteria?.start;
    controls[FilterValidation.controlStartToShow] = new FormControl(this.getFormattedDateForFieldType(startForDisplay, valueField?.type) || '');
    controls[FilterValidation.controlEndToShow] = new FormControl(this.getFormattedDateForFieldType(criteria?.end, valueField?.type) || '');

    this.form.addControl(
      `filter${this.filterIterator++}`,
      this.formBuilder.group(controls, {
        validators: [this.filterValidation.getFilterGroupValidator()]
      })
    );
  }

  removeFilter(name: string): void {
    if (this.getFieldGroupValue(name).value?.type == FieldType.AREA) {
      this.mapEventService?.clearPolygon(name);
      this.mapEventService?.cancelDrawPolygon(name);
    }
    this.form.removeControl(name);
  }

  getFilters(): RequestCriteria {
    const criteria: RequestCriteria[] = this.formFilters.map(filterKey => {
      const filterGroup = this.form.get(filterKey);
      const field: ValueField = filterGroup.get(FilterValidation.controlField).value;
      const operator: Operator = filterGroup.get(FilterValidation.controlOperator).value;
      const valueControlValue = filterGroup.get(FilterValidation.controlValue).value;
      const startControlValue = filterGroup.get(FilterValidation.controlStart).value;
      const endControlValue = filterGroup.get(FilterValidation.controlEnd).value;
      let value;
      let start;
      let end;
      switch (field.type) {
        case FieldType.BOOLEAN:
          value = valueControlValue;
          break;
        case FieldType.FLOAT:
          if (operator === Operator.between || operator === Operator.betweenInclusive) {
            const toFloat = (v: any): number | undefined => {
              const s = v?.toString()?.trim()?.replace(',', '.');
              return s != null && s !== '' ? +s : undefined;
            };
            start = toFloat(startControlValue);
            end = toFloat(endControlValue);
          } else if (isArray(valueControlValue))
            value = valueControlValue.map(val => {
              const transformed = val?.toString()?.trim()?.replace(',', '.');
              if (transformed != null)
                return +transformed;
              return null;
            });
          else {
            value = valueControlValue?.toString()?.trim()?.replace(',', '.');
            if (value != null)
              value = +value;
          }
          break;
        case FieldType.INTEGER:
          if (operator === Operator.between || operator === Operator.betweenInclusive) {
            start = startControlValue != null && startControlValue !== '' ? +startControlValue : undefined;
            end = endControlValue != null && endControlValue !== '' ? +endControlValue : undefined;
          } else if (isArray(valueControlValue))
            value = valueControlValue.map(val => val != null ? +val : null);
          else if (valueControlValue != null)
            value = +valueControlValue;
          break;
        case FieldType.DATETIME:
          if (operator === Operator.equals) {
            value = this.toDateValues(valueControlValue).map(m => m.toISOString());
          } else if (startControlValue) {
            start = (startControlValue as Moment).toISOString();
            if (endControlValue)
              end = (endControlValue as Moment).toISOString();
          }
          break;
        case FieldType.DATE:
          if (operator === Operator.equals) {
            value = this.toDateValues(valueControlValue).map(m => m.toDateFormat());
          } else {
            start = (startControlValue as Moment).toDateFormat();
            end = (endControlValue as Moment).toDateFormat();
          }
          break;
        case FieldType.TIME:
          start = startControlValue;
          end = endControlValue;
          break;
        default:
          if (isArray(valueControlValue))
            value = valueControlValue.map(val => val?.toString()?.trim());
          else
            value = valueControlValue?.toString()?.trim();
          break;
      }

      if (isArray(value) && value.length > 1) {
        const valueCriteria: RequestCriteria[] = value.map(val => ({
          fieldName: field.fieldName || field.name,
          operator,
          value: val != null ? val : undefined,
          start: (start !== null && start !== undefined && start !== '') ? start : undefined,
          end: (end !== null && end !== undefined && end !== '') ? end : undefined
        }));
        return {
          operator: Operator.or,
          criteria: valueCriteria
        };
      } else {
        let valueToUse;
        if (value != null) {
          if (isArray(value))
            valueToUse = value[0];
          else
            valueToUse = value;
        }
        return {
          fieldName: field.fieldName || field.name,
          operator,
          value: valueToUse,
          start: (start !== null && start !== undefined && start !== '') ? start : undefined,
          end: (end !== null && end !== undefined && end !== '') ? end : undefined
        };
      }
    });

    return {
      operator: Operator.and,
      criteria
    };
  }

  validate(): boolean {
    validateAllFormFields(this.form);
    if (this.form.valid) {
      return true;
    } else {
      return false;
    }
  }

  validateForSetOfFilters(): boolean {
    if (getErrorsForSetOfFilters(this.form)) {
      this.toastService.showErrorToast('fillFormCorrectly');
      return false;
    } else {
      return true;
    }
  }

  openVehicleGroupDialog(): void {
    const dialogRef = this.dialog.open(VehicleGroupDialogComponent);

    dialogRef.afterClosed().subscribe(result => {
      if (result?.updated) {
        this.filterBuilderListFieldComponents
          .filter(component => component.fieldType === FilterListFieldType.VEHICLE_GROUP)
          .forEach(component => {
            this.filterDataProvider.updateDataForField(component.fieldType);
            component.resetState();
          });
      }
    });
  }

  shouldAllowVehicleGroupsEditing(): boolean {
    return this.settings.shouldAllowVehicleGroupEditing();
  }

  canCopyFromSelection(groupName: string): boolean {
    if (!this.selectedRows?.length) return false;
    const field: ValueField = this.getFieldGroupValue(groupName)?.value;
    if (!field) return false;
    const operator: Operator = this.getFieldGroupOperator(groupName)?.value;
    if (!operator) return false;
    if (operator === Operator.isNull || operator === Operator.notNull) return false;
    if (field.type === FieldType.AREA) return false;
    return true;
  }

  copyValuesFromSelection(groupName: string): void {
    const field: ValueField = this.getFieldGroupValue(groupName)?.value;
    const operator: Operator = this.getFieldGroupOperator(groupName)?.value;
    if (!field || !operator) return;

    const fieldKey = field.fieldName || field.name;
    const rawValues: any[] = (this.selectedRows || []).reduce((acc: any[], row: any) => {
      const v = field.rowValueExtractor ? field.rowValueExtractor(row) : row[fieldKey];
      return acc.concat(Array.isArray(v) ? v : [v]);
    }, []).filter((val: any) => val != null && val !== '');

    if (!rawValues.length) return;

    const filterGroup = this.form.get(groupName);
    const isBetween = operator === Operator.between || operator === Operator.betweenInclusive;

    const multivalue = this.isMultiValueOperator(operator);

    switch (field.type) {
      case FieldType.BOOLEAN: {
        const uniqueBools = [...new Set(rawValues.map(v => v === true || v === 'true' || v === 1))];
        const valueArray = this.getFieldGroupArray(groupName);
        if (multivalue) {
          while (valueArray.length > 0) valueArray.removeAt(0);
          uniqueBools.forEach(v => valueArray.push(new FormControl(v)));
        } else {
          valueArray.controls[0]?.setValue(uniqueBools[0]);
        }
        break;
      }
      case FieldType.DATE:
      case FieldType.DATETIME: {
        const moments = rawValues.map(v => moment(v)).filter(m => m.isValid());
        if (!moments.length) return;
        const minDate = moments.reduce((a, b) => a.isBefore(b) ? a : b);
        const maxDate = moments.reduce((a, b) => a.isAfter(b) ? a : b);
        if (isBetween) {
          filterGroup.get(FilterValidation.controlStart).setValue(minDate);
          filterGroup.get(FilterValidation.controlEnd).setValue(maxDate);
          this.onDateValueChanged(minDate, groupName, 'start', field.type);
          this.onDateValueChanged(maxDate, groupName, 'end', field.type);
        } else if (multivalue) {
          // equals: append unique moments to controlValue, update display to minDate
          const valueArray = this.getFieldGroupArray(groupName);
          const fmtKey = (v: any): string | null => {
            if (!v) return null;
            if (typeof (v as Moment).format === 'function')
              return field.type === FieldType.DATE ? (v as Moment).format('YYYY-MM-DD') : (v as Moment).toISOString();
            if (typeof v === 'string' && v) return v;
            return null;
          };
          const existingKeys = new Set(valueArray.controls.map(c => fmtKey(c.value)).filter(Boolean));
          const uniqueNew = moments.filter(m => !existingKeys.has(fmtKey(m)));
          if (!uniqueNew.length) return;

          if (existingKeys.size + uniqueNew.length > FilterBuilderComponent.HARD_LIMIT_VALUES) {
            this.toastService.showWarnToast(
              'copyValuesFromSelectionTooManyValues', null,
              { total: existingKeys.size + uniqueNew.length, max: FilterBuilderComponent.HARD_LIMIT_VALUES }
            );
            return;
          }

          const doAppendDates = (): void => {
            for (let i = valueArray.controls.length - 1; i >= 0; i--) {
              if (!(valueArray.controls[i].value)?.isValid?.()) valueArray.removeAt(i);
            }
            uniqueNew.forEach(m => valueArray.push(new FormControl(m)));
            if (valueArray.controls.length === 0) valueArray.push(new FormControl(''));
            filterGroup.get(FilterValidation.controlStart).setValue(minDate);
            this.onDateValueChanged(minDate, groupName, 'start', field.type);
            this.changeDetector.detectChanges();
          };

          if (uniqueNew.length > 10) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
              data: { message: 'copyValuesFromSelectionManyValues', messageParams: { count: uniqueNew.length } }
            });
            dialogRef.afterClosed().subscribe((confirmed: boolean) => { if (confirmed) doAppendDates(); });
          } else {
            doAppendDates();
          }
        } else {
          // single-value: greaterThan/greaterOrEqual → max, pozostałe → min
          const isGreater = operator === Operator.greaterThan || operator === Operator.greaterOrEqual;
          const dateToUse = isGreater ? maxDate : minDate;
          filterGroup.get(FilterValidation.controlStart).setValue(dateToUse);
          this.onDateValueChanged(dateToUse, groupName, 'start', field.type);
        }
        break;
      }
      case FieldType.TIME: {
        const strTimes = rawValues.map(v => String(v)).filter(v => /^\d{2}:\d{2}/.test(v));
        if (!strTimes.length) return;
        const sorted = [...strTimes].sort();
        filterGroup.get(FilterValidation.controlStart).setValue(sorted[0]);
        filterGroup.get(FilterValidation.controlEnd).setValue(sorted[sorted.length - 1]);
        break;
      }
      case FieldType.INTEGER: {
        const nums = rawValues.map(v => parseInt(String(v).replace(/[^\d-]/g, ''), 10)).filter(n => !isNaN(n));
        if (!nums.length) return;
        if (isBetween) {
          filterGroup.get(FilterValidation.controlStart).setValue(String(Math.min(...nums)));
          filterGroup.get(FilterValidation.controlEnd).setValue(String(Math.max(...nums)));
        } else if (multivalue) {
          const parsed: string[] = Array.from(new Set<string>(nums.map(n => String(n))));
          this.appendToValueArray(groupName, parsed, true, 10);
        } else if (this.isNegativeMultivalueOperator(operator)) {
          const parsed: string[] = Array.from(new Set<string>(nums.map(n => String(n))));
          this.addCopiedNegativeCriteria(groupName, field, operator, parsed);
        } else {
          const isGreater = operator === Operator.greaterThan || operator === Operator.greaterOrEqual;
          this.appendToValueArray(groupName, [String(isGreater ? Math.max(...nums) : Math.min(...nums))], false);
        }
        break;
      }
      case FieldType.FLOAT: {
        const nums = rawValues.map(v => parseFloat(String(v).replace(',', '.').replace(/[^\d.-]/g, ''))).filter(n => !isNaN(n));
        if (!nums.length) return;
        if (isBetween) {
          filterGroup.get(FilterValidation.controlStart).setValue(String(Math.min(...nums)));
          filterGroup.get(FilterValidation.controlEnd).setValue(String(Math.max(...nums)));
        } else if (multivalue) {
          const parsed: string[] = Array.from(new Set<string>(nums.map(n => String(n))));
          this.appendToValueArray(groupName, parsed, true, 10);
        } else if (this.isNegativeMultivalueOperator(operator)) {
          const parsed: string[] = Array.from(new Set<string>(nums.map(n => String(n))));
          this.addCopiedNegativeCriteria(groupName, field, operator, parsed);
        } else {
          const isGreater = operator === Operator.greaterThan || operator === Operator.greaterOrEqual;
          this.appendToValueArray(groupName, [String(isGreater ? Math.max(...nums) : Math.min(...nums))], false);
        }
        break;
      }
      default: {
        const strValues: string[] = Array.from(new Set<string>(rawValues.map(v => String(v))));
        if (multivalue) {
          this.appendToValueArray(groupName, strValues, true, 10);
        } else if (this.isNegativeMultivalueOperator(operator)) {
          this.addCopiedNegativeCriteria(groupName, field, operator, strValues);
        } else {
          this.appendToValueArray(groupName, strValues, false);
        }
        break;
      }
    }
    this.changeDetector.detectChanges();
  }

  private appendToValueArray(groupName: string, newValues: string[], multivalue: boolean = true, confirmThreshold?: number): void {
    const valueArray = this.getFieldGroupArray(groupName);
    if (!multivalue) {
      if (newValues.length) valueArray.controls[0]?.setValue(newValues[0]);
      return;
    }
    const existingValues = new Set(
      valueArray.controls
        .map(c => c.value)
        .filter(v => v != null && v !== '')
        .map(v => String(v))
    );
    const uniqueNew = newValues.filter(v => !existingValues.has(v));
    if (!uniqueNew.length) return;

    if (existingValues.size + uniqueNew.length > FilterBuilderComponent.HARD_LIMIT_VALUES) {
      this.toastService.showWarnToast(
        'copyValuesFromSelectionTooManyValues', null,
        { total: existingValues.size + uniqueNew.length, max: FilterBuilderComponent.HARD_LIMIT_VALUES }
      );
      return;
    }

    const doAppend = (): void => {
      for (let i = valueArray.controls.length - 1; i >= 0; i--) {
        const val = valueArray.controls[i].value;
        if (val === '' || val == null) valueArray.removeAt(i);
      }
      uniqueNew.forEach(val => valueArray.push(new FormControl(val)));
      if (valueArray.controls.length === 0) valueArray.push(new FormControl(''));
      this.changeDetector.detectChanges();
    };

    if (confirmThreshold != null && uniqueNew.length > confirmThreshold) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: { message: 'copyValuesFromSelectionManyValues', messageParams: { count: uniqueNew.length } }
      });
      dialogRef.afterClosed().subscribe((confirmed: boolean) => { if (confirmed) doAppend(); });
    } else {
      doAppend();
    }
  }

  private getListFieldType(groupName: string): FilterListFieldType {
    const valueField = this.getFieldGroupValue(groupName).value;
    if (valueField == null)
      return null;
    else {
      if (valueField.type === FieldType.STOP_PILE_EVENT_TYPE_RAW)
        return FilterListFieldType.STOP_PILE_EVENT_TYPE_RAW;
      if (valueField.type === FieldType.STOP_PILE_EVENT_TYPE_STAT)
        return FilterListFieldType.STOP_PILE_EVENT_TYPE_STAT;
      if (valueField.type === FieldType.VEHICLE_GROUP)
        return FilterListFieldType.VEHICLE_GROUP;
      const name = valueField?.name;
      if (isEmpty(name)) return null;
      switch (name) {
        case DepartureFields.RT_VEHICLE_SIDE_NUMBER:
          return FilterListFieldType.VEH_SIDE_NUMBER;
        case DepartureFields.COURSE_DIRECTION:
          return FilterListFieldType.DIRECTION_NAME;
        case DepartureFields.COURSE_LINE:
          return FilterListFieldType.LINE;
        case DepartureFields.STOP_CODE:
          return FilterListFieldType.SP_CODE;
        default:
          return FilterListFieldType[name.toUpperCase()];
      }
    }
  }

  private setReadOnlyMode(initialFilters: RequestCriteria): void {
    this.readOnlyMode = true;
    this.initForm();
    this.addFiltersIfInitialFilters(initialFilters);
  }

  private addFiltersIfInitialFilters(initialFilters: RequestCriteria): void {
    if (initialFilters?.criteria) {
      initialFilters.criteria.forEach(mainCriteria => {
        if (mainCriteria.operator == Operator.or) {
          if (mainCriteria.criteria?.length) {
            const values = mainCriteria.criteria.map(criteria => criteria.value);
            this.addFilter(mainCriteria.criteria[0], values);
          } else {
            console.warn('Invalid criteria inside OR operator criterion', mainCriteria);
          }
        } else {
          this.addFilter(mainCriteria);
        }
      });
      this.changeDetector.detectChanges();
    }
  }

  // Kryterium 'value' bywa zasiane pustą kontrolką (addFilter dla zakresu), a operator potrafi
  // przeskoczyć na "równe" zanim wartość zostanie uzupełniona - takie wpisy trzeba pominąć.
  private toDateValues(controlValue: any): Moment[] {
    return (isArray(controlValue) ? controlValue : [controlValue])
      .filter(val => val != null && val !== '')
      .map(val => moment.isMoment(val) ? val : moment(val))
      .filter(date => date.isValid());
  }

  private getValueFieldByName(name: string): ValueField | null {
    const idx = this.fields.findIndex(field => field.name == name || field.fieldName == name);
    if (idx > -1)
      return this.fields[idx];
    return null;
  }

  getFormattedDateForFieldType(date: Moment | string | null, fieldType: FieldType | null): string | null {
    if (date && (fieldType == FieldType.DATETIME || fieldType == FieldType.DATE)) {
      if (typeof (date) == 'string')
        date = moment(date);
      if (fieldType == FieldType.DATE)
        return date.toVisibleDateFormat();
      else if (fieldType == FieldType.DATETIME)
        return date.toVisibleDateTimeFormat();
    }
    return null;
  }
}
